export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// Keys the backend uses for the human-readable explanation, most specific first.
const MESSAGE_KEYS = [
  'message',
  'detail',
  'non_field_errors',
  'error',
  'errors',
];

// Keys that carry machine-readable metadata rather than an explanation.
const MACHINE_KEYS = ['code', 'status', 'status_code', 'error_type', 'type'];

/** A server error page, not something a sourcer can act on. */
export const isHtmlError = (value: string) => value.trim().startsWith('<');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const humanizeFieldName = (key: string) => key.replace(/_/g, ' ');

/**
 * Some machine keys double as serializer field names — a search can be patched
 * with a `status`, so `{ status: ['This transition is not allowed.'] }` is a
 * field error, while `{ status: 400 }` is metadata. Scalars are metadata;
 * arrays and objects have the field-error shape.
 */
const isMachineMetadata = (key: string, value: unknown) =>
  MACHINE_KEYS.includes(key) && !Array.isArray(value) && !isRecord(value);

const firstExplanation = (values: unknown[]): string | null =>
  values.reduce<string | null>(
    (found, value) => found ?? extractErrorMessage(value),
    null,
  );

const fromString = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (isHtmlError(trimmed)) return null;
  return trimmed;
};

const fromNamedKeys = (record: Record<string, unknown>): string | null => {
  const present = MESSAGE_KEYS.filter((key) => key in record);
  return firstExplanation(present.map((key) => record[key]));
};

/**
 * Field errors, e.g. `{ phone: ['Enter a valid phone number.'] }`. Keeps the
 * field name so the sourcer knows what to fix. Reports the first field that
 * failed — showing every one at once makes for an unreadable toast.
 */
const fromFieldErrors = (record: Record<string, unknown>): string | null => {
  const actionable = Object.entries(record).filter(
    ([key, value]) => !isMachineMetadata(key, value),
  );

  return actionable.reduce<string | null>((found, [key, value]) => {
    if (found) return found;
    const explanation = extractErrorMessage(value);
    return explanation ? `${humanizeFieldName(key)}: ${explanation}` : null;
  }, null);
};

/**
 * Pull the human-readable explanation out of an error response body.
 *
 * The backend is not consistent about the shape it returns — a plain string,
 * `{ message, code }`, `{ errors: { ... } }`, and field errors are all
 * possible. Returns null when the body carries no explanation, so the caller
 * can fall back to generic copy rather than showing a bare error number.
 */
export function extractErrorMessage(data: unknown): string | null {
  if (typeof data === 'string') return fromString(data);
  if (Array.isArray(data)) return firstExplanation(data);
  if (!isRecord(data)) return null;

  return fromNamedKeys(data) ?? fromFieldErrors(data);
}
